import { z } from "zod"

import { agentKinds } from "../../agents/vocabulary.js"
import { exportFormats, exportTemplates } from "../../taxonomy.js"
import { ApiUserSummarySchema } from "./users.js"

export const ApiResolveFlagBodySchema = z.object({
  status: z.enum(["resolved", "dismissed"]),
  action: z.enum(["confirm", "dismiss", "reject"]).optional(),
  rationale: z.string().max(2000).optional(),
})

export type ApiResolveFlagBody = z.infer<typeof ApiResolveFlagBodySchema>

export const ApiRecordPatchBodySchema = z.object({
  reviewFlagId: z.string().uuid().optional(),
  fieldPath: z.string().min(1).max(200),
  valueBefore: z.unknown().optional(),
  valueAfter: z.unknown(),
  rationale: z.string().max(2000).optional(),
})

export type ApiRecordPatchBody = z.infer<typeof ApiRecordPatchBodySchema>

export const ApiReviewDecisionResponseSchema = z.object({
  reviewDecisionId: z.string().uuid(),
  action: z.string(),
  fieldPath: z.string().nullable(),
  decidedByUserId: z.string(),
  decidedAt: z.string().datetime(),
})

export const ApiResolveFlagResponseSchema = z.object({
  reviewFlag: z.object({
    flagId: z.string().uuid(),
    status: z.string(),
    resolvedAt: z.string().datetime().nullable(),
  }),
  reviewDecision: ApiReviewDecisionResponseSchema,
  documentState: z.string(),
})

export type ApiResolveFlagResponse = z.infer<
  typeof ApiResolveFlagResponseSchema
>

export const ApiCreateRecordPatchResponseSchema = z.object({
  recordPatch: z.object({
    recordPatchId: z.string().uuid(),
    // Null once a re-read replaced the record rows: the patch history
    // outlives the rows it edited.
    extractedRecordId: z.string().uuid().nullable(),
    fieldPath: z.string(),
    valueBefore: z.unknown().optional(),
    valueAfter: z.unknown(),
    createdByUserId: z.string(),
    createdAt: z.string().datetime(),
  }),
  reviewDecision: ApiReviewDecisionResponseSchema,
  documentState: z.string(),
})

export type ApiCreateRecordPatchResponse = z.infer<
  typeof ApiCreateRecordPatchResponseSchema
>

export const ApiSummaryPatchBodySchema = z.object({
  reviewFlagId: z.string().uuid().optional(),
  /** "summary.<field>" (matching flag fieldPaths) or the bare field name. */
  fieldPath: z.string().min(1).max(200),
  valueBefore: z.unknown().optional(),
  valueAfter: z.unknown(),
  rationale: z.string().max(2000).optional(),
})

export type ApiSummaryPatchBody = z.infer<typeof ApiSummaryPatchBodySchema>

export const ApiCreateSummaryPatchResponseSchema = z.object({
  summaryPatch: z.object({
    summaryPatchId: z.string().uuid(),
    fieldPath: z.string(),
    valueBefore: z.unknown().optional(),
    valueAfter: z.unknown(),
    createdByUserId: z.string(),
    createdAt: z.string().datetime(),
  }),
  reviewDecision: ApiReviewDecisionResponseSchema,
  documentState: z.string(),
  /** The summary after the patch and the automatic recheck. */
  summary: z.record(z.string(), z.unknown()),
})

export type ApiCreateSummaryPatchResponse = z.infer<
  typeof ApiCreateSummaryPatchResponseSchema
>

export const ApiApprovalResponseSchema = z.object({
  approvalId: z.string().uuid(),
  documentId: z.string().uuid(),
  projectionHash: z.string(),
  /** Null exactly when an agent approved; `agentKind` names it instead. */
  approvedByUserId: z.string().nullable(),
  /** The agent kind that approved, e.g. "bills"; null for a person. The
   * vocabulary itself, not a loose string: the column is that enum, and a
   * client that cannot narrow this cannot name the agent on the screen. */
  agentKind: z.enum(agentKinds).nullable().optional(),
  // Mirrored Clerk profile of the approver; null when unresolved. Optional
  // so older API deployments still parse.
  approvedBy: ApiUserSummarySchema.nullable().optional(),
  approvedAt: z.string().datetime(),
  idempotentReplay: z.boolean(),
})

export type ApiApprovalResponse = z.infer<typeof ApiApprovalResponseSchema>

export const ApiRevokeApprovalResponseSchema = z.object({
  approvalId: z.string().uuid(),
  documentId: z.string().uuid(),
  revokedByUserId: z.string(),
  // Mirrored Clerk profile of the revoker; null when unresolved. Optional
  // so older API deployments still parse.
  revokedBy: ApiUserSummarySchema.nullable().optional(),
  revokedAt: z.string().datetime(),
  documentState: z.string(),
})

export type ApiRevokeApprovalResponse = z.infer<
  typeof ApiRevokeApprovalResponseSchema
>

// A bulk selection is always an explicit id list, capped at the list
// endpoint's own limit. Each entry carries its own projectionHash so the
// staleness check behaves exactly like single approval.
export const ApiBulkApproveDocumentsBodySchema = z.object({
  documents: z
    .array(
      z.object({
        documentId: z.string().uuid(),
        projectionHash: z.string().min(1).max(200).optional(),
      })
    )
    .min(1)
    .max(100),
})

export type ApiBulkApproveDocumentsBody = z.infer<
  typeof ApiBulkApproveDocumentsBodySchema
>

export const ApiBulkApprovalResultSchema = z.object({
  documentId: z.string().uuid(),
  status: z.enum(["approved", "replayed", "skipped"]),
  // The repository error code, unwrapped: approval_stale, approval_conflict,
  // open_required_flags, projection_not_found, document_not_found.
  code: z.string().optional(),
  message: z.string().optional(),
})

export type ApiBulkApprovalResult = z.infer<typeof ApiBulkApprovalResultSchema>

export const ApiBulkApproveDocumentsResponseSchema = z.object({
  /** Documents that are approved now, including idempotent replays. */
  approved: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  results: z.array(ApiBulkApprovalResultSchema),
})

export type ApiBulkApproveDocumentsResponse = z.infer<
  typeof ApiBulkApproveDocumentsResponseSchema
>

export const ApiCreateExportBodySchema = z.object({
  format: z.enum(exportFormats),
  template: z.enum(exportTemplates).optional(),
})

export type ApiCreateExportBody = z.infer<typeof ApiCreateExportBodySchema>

export const ApiExportResponseSchema = z.object({
  exportId: z.string().uuid(),
  artifactId: z.string().uuid(),
  format: z.string(),
  state: z.string(),
  projectionHash: z.string(),
  isApproved: z.boolean(),
  isStale: z.boolean(),
  artifact: z.object({
    artifactId: z.string().uuid(),
    kind: z.string(),
    container: z.string(),
    blobPath: z.string(),
    contentHash: z.string(),
    byteSize: z.number().int().nonnegative(),
    mimeType: z.string(),
    createdAt: z.string().datetime(),
  }),
})

export type ApiExportResponse = z.infer<typeof ApiExportResponseSchema>

export const ApiBulkCreateExportBodySchema = z.object({
  documentIds: z.array(z.string().uuid()).min(1).max(100),
  format: z.enum(exportFormats),
  template: z.enum(exportTemplates).optional(),
})

export type ApiBulkCreateExportBody = z.infer<
  typeof ApiBulkCreateExportBodySchema
>

export const ApiBulkExportResponseSchema = z.object({
  artifactId: z.string().uuid(),
  /** One export row per contributing document, all sharing the artifact. */
  exportIds: z.array(z.string().uuid()),
  format: z.string(),
  template: z.string(),
  documentCount: z.number().int().nonnegative(),
  rowCount: z.number().int().nonnegative(),
  byteSize: z.number().int().nonnegative(),
  // Documents with no projection are skipped rather than aborting the file.
  skipped: z.array(
    z.object({ documentId: z.string().uuid(), code: z.string() })
  ),
})

export type ApiBulkExportResponse = z.infer<typeof ApiBulkExportResponseSchema>

export const ApiDocumentExportListResponseSchema = z.object({
  exports: z.array(
    z.object({
      exportId: z.string().uuid(),
      artifactId: z.string().uuid(),
      format: z.string(),
      state: z.string(),
      isStale: z.boolean(),
      isApproved: z.boolean(),
      createdAt: z.string().datetime(),
      requestedByUserId: z.string().nullable(),
      // Mirrored Clerk profile of the requester; null when unresolved.
      requestedBy: ApiUserSummarySchema.nullable().optional(),
      byteSize: z.number().int().nonnegative(),
      retentionState: z.string(),
      expiresAt: z.string().datetime().nullable(),
      // Exports sharing the artifact: 1 for a single export, N for a merged one.
      documentCount: z.number().int().positive(),
    })
  ),
})

export type ApiDocumentExportListResponse = z.infer<
  typeof ApiDocumentExportListResponseSchema
>

export type ApiDocumentExportListItem =
  ApiDocumentExportListResponse["exports"][number]

export const ApiExportAccessResponseSchema = z.object({
  artifactId: z.string().uuid(),
  accessUrl: z.string().url(),
  expiresAt: z.string().datetime(),
  headers: z.record(z.string(), z.string()),
})

export type ApiExportAccessResponse = z.infer<
  typeof ApiExportAccessResponseSchema
>

export const ApiCreatePostingBodySchema = z.object({
  postingMappingId: z.string().uuid(),
  idempotencyKey: z.string().min(1).max(200),
})

export type ApiCreatePostingBody = z.infer<typeof ApiCreatePostingBodySchema>

export const ApiPostingResponseSchema = z.object({
  postingId: z.string().uuid(),
  documentId: z.string().uuid(),
  postingMappingId: z.string().uuid(),
  // Null on a posting a workflow made from a pre-approval trigger.
  documentApprovalId: z.string().uuid().nullable(),
  state: z.string(),
  idempotencyKey: z.string(),
  requestedByUserId: z.string(),
  requestedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  attempts: z.array(
    z.object({
      postingAttemptId: z.string().uuid(),
      attemptNumber: z.number().int(),
      state: z.string(),
      externalDestinationId: z.string().nullable(),
      externalObjectId: z.string().nullable(),
      errorClassification: z.string().nullable(),
      errorCode: z.string().nullable(),
      errorMessage: z.string().nullable(),
      startedAt: z.string().datetime().nullable(),
      completedAt: z.string().datetime().nullable(),
    })
  ),
  idempotentReplay: z.boolean(),
})

export type ApiPostingResponse = z.infer<typeof ApiPostingResponseSchema>

export const ApiPostingMappingResponseSchema = z.object({
  postingMappingId: z.string().uuid(),
  destination: z.string(),
  name: z.string(),
  documentTypeVersionId: z.string().uuid().nullable(),
  mapping: z.record(z.string(), z.unknown()),
  createdAt: z.string().datetime(),
})

export type ApiPostingMappingResponse = z.infer<
  typeof ApiPostingMappingResponseSchema
>

export const ApiPostingMappingListResponseSchema = z.object({
  postingMappings: z.array(ApiPostingMappingResponseSchema),
})

export type ApiPostingMappingListResponse = z.infer<
  typeof ApiPostingMappingListResponseSchema
>

export const ApiDocumentPostingListResponseSchema = z.object({
  postings: z.array(ApiPostingResponseSchema),
})

export type ApiDocumentPostingListResponse = z.infer<
  typeof ApiDocumentPostingListResponseSchema
>
